import { Environment } from '../../environment.js'
import sentryTraceHeaders from '../Sentry.js'
import type { ChatRequest } from '../../Generated/ChatRequest.ts'
import type { ChatStreamEvent } from '../../Generated/ChatStreamEvent.ts'
import type { ChatHealth } from '../../Generated/ChatHealth.ts'
import type { ChatDigest } from '../../Generated/ChatDigest.ts'
import type { ReportBlock } from '../../Generated/ReportBlock.ts'
import type { ReportRenderRequest } from '../../Generated/ReportRenderRequest.ts'
import type { ReportRenderResponse } from '../../Generated/ReportRenderResponse.ts'
import type { FindingsShareRequest } from '../../Generated/FindingsShareRequest.ts'
import type { FindingsShareResponse } from '../../Generated/FindingsShareResponse.ts'
import { HttpStreamError } from '../../View/Page/OrderManagementPage/Modals/Krona/Streaming/HttpStreamError.ts'

/**
 * Fast readiness canary for the chat LLM backend (`GET /api/chat/health`, a ~70ms llama.cpp `/health` probe
 * server-side). Never throws — resolves `false` on any error/non-OK so the UI can show an offline state.
 */
export async function getChatHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${Environment.API_URL}/api/chat/health`, {
      credentials: 'include',
      headers: { ...sentryTraceHeaders() }
    })
    if (!res.ok) return false
    return ((await res.json()) as ChatHealth).available === true
  } catch {
    return false
  }
}

/**
 * Deep-view fetch for a provenance `EvidenceRef` (Epic A #7). GETs the full detail of the underlying entity
 * (`otu` / `sample`) for the given conversation scope + anchor from `/api/chat/evidence/:kind`, which delegates
 * to the same access-gated chat services the tools use (no new query). Cookie-authenticated like the rest.
 * The backend returns the service's own JSON object shape, so this resolves a generic `Record` the card renders
 * as key→value sections. Throws on a non-OK status (surfaced as the card's error state). Not used for `report`
 * (that deep-view links to the order page instead).
 */
export async function getEvidenceDetail(
  kind: string,
  scopeType: string,
  scopeId: string,
  anchor: string,
  // SPIN threading (Epic A #7): a SPIN-mode OTU ref anchors on a spin id, so the deep-view must resolve it via
  // the SPIN detail path. Passed through as query params only when present; classic OTUs omit them.
  spinAlgorithm?: string,
  spinAtTime?: string
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams({ scopeType, scopeId, anchor })
  if (spinAlgorithm) params.set('spinAlgorithm', spinAlgorithm)
  if (spinAtTime) params.set('spinAtTime', spinAtTime)
  const res = await fetch(`${Environment.API_URL}/api/chat/evidence/${kind}?${params.toString()}`, {
    credentials: 'include',
    headers: { ...sentryTraceHeaders() }
  })
  if (!res.ok) {
    let message = `Could not load detail (${res.status})`
    try {
      const data = await res.json()
      if (typeof data?.message === 'string') message = data.message
    } catch {
      // body was not JSON — keep the generic message
    }
    throw new Error(message)
  }
  return (await res.json()) as Record<string, unknown>
}

/**
 * Build the URL of the server-rendered evidence DOSSIER for an OTU finding (Epic A #12). The endpoint returns a
 * complete, self-contained HTML page (not JSON), so callers `window.open` this in a new tab for the browser to
 * render + print-to-PDF — there is no fetch here. Mirrors {@link getEvidenceDetail}'s params (scope + anchor +
 * optional SPIN threading) and the same `Environment.API_URL` base every other chat call uses.
 */
export function buildDossierUrl(
  scopeType: string,
  scopeId: string,
  anchor: string,
  spinAlgorithm?: string,
  spinAtTime?: string
): string {
  const params = new URLSearchParams({ scopeType, scopeId, anchor })
  if (spinAlgorithm) params.set('spinAlgorithm', spinAlgorithm)
  if (spinAtTime) params.set('spinAtTime', spinAtTime)
  return `${Environment.API_URL}/api/chat/dossier/otu?${params.toString()}`
}

/**
 * Passive "read this first" scope OVERVIEW (D-digest, roadmap #4). GETs `/api/chat/digest` for the current
 * scope; the backend composes a plain-language exec summary ONCE server-side (reusing the chat engine + tools +
 * honesty layer) and caches it. The FIRST request for a scope is slow (20–60s — a full agent loop); later ones
 * return instantly (`cached: true`). Cookie-authenticated like the rest. Throws on a non-OK status (incl. the
 * clean 503 when the LLM backend is unavailable, and 404 when the digest is config-disabled) so the card can
 * render its error state instead of breaking the page.
 */
export async function getChatDigest(scopeType: string, scopeId: string): Promise<ChatDigest> {
  const params = new URLSearchParams({ scopeType, scopeId })
  const res = await fetch(`${Environment.API_URL}/api/chat/digest?${params.toString()}`, {
    credentials: 'include',
    headers: { ...sentryTraceHeaders() }
  })
  if (!res.ok) {
    let message = `Could not load the overview (${res.status})`
    try {
      const data = await res.json()
      if (typeof data?.message === 'string') message = data.message
    } catch {
      // body was not JSON — keep the generic message
    }
    throw new Error(message)
  }
  return (await res.json()) as ChatDigest
}

/**
 * Mint a no-login "D-share" findings snapshot (roadmap #6). ORDER scope only. POSTs to `/api/chat/share`,
 * which — AS THE LOGGED-IN USER — composes/reuses the scope digest, freezes it to self-contained HTML, and
 * stores it keyed by the order's share checksum, returning the relative public path of the anonymous serve
 * endpoint. We prepend the same `Environment.API_URL` base every other chat call uses to build the absolute,
 * no-login shareable URL (the recipient opens it directly — it renders the frozen HTML with no login).
 * Cookie-authenticated like the rest. Throws on a non-OK status (incl. 403 when the caller can't share this
 * order, 404 when sharing is config-disabled) so the caller can surface an error state.
 */
export async function shareFindings(scopeType: string, scopeId: string): Promise<string> {
  const body: FindingsShareRequest = { scopeType, scopeId }
  const res = await fetch(`${Environment.API_URL}/api/chat/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sentryTraceHeaders() },
    credentials: 'include',
    body: JSON.stringify(body)
  })
  if (!res.ok) {
    let message = `Could not create a share link (${res.status})`
    try {
      const data = await res.json()
      if (typeof data?.message === 'string') message = data.message
    } catch {
      // body was not JSON — keep the generic message
    }
    throw new Error(message)
  }
  const data = (await res.json()) as FindingsShareResponse
  return `${Environment.API_URL}${data.path}`
}

/**
 * Render the agent-authored report document (IR-1) to a self-contained, print-styled HTML page. POSTs the
 * current blocks + title + scope to `/api/chat/report/render`; the backend runs the recipe blocks against the
 * same scope-gated chat services and returns the composed `{ html, readback }`. Cookie-authenticated like the
 * rest. Throws on a non-OK status so the epic can flip the panel into its error state (never crashing chat).
 */
export async function renderReportDoc(
  scopeType: string,
  scopeId: string,
  blocks: ReportBlock[],
  title?: string
): Promise<ReportRenderResponse> {
  const body: ReportRenderRequest = { scopeType, scopeId, blocks, title }
  const res = await fetch(`${Environment.API_URL}/api/chat/report/render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sentryTraceHeaders() },
    credentials: 'include',
    body: JSON.stringify(body)
  })
  if (!res.ok) {
    let message = `Could not render the report (${res.status})`
    try {
      const data = await res.json()
      if (typeof data?.message === 'string') message = data.message
    } catch {
      // body was not JSON — keep the generic message
    }
    throw new Error(message)
  }
  return (await res.json()) as ReportRenderResponse
}

/** Callbacks the SSE reader invokes as each event arrives; mirrors the backend `ChatStreamEvent` types. */
export interface StreamChatCallbacks {
  /** A tool call finished (drives the live "Searching OTUs…" line). */
  onStep?: (event: ChatStreamEvent) => void
  /** A chunk of the answer prose. */
  onDelta?: (text: string) => void
  /** Terminal canvas payload (directives / focus / chartUpdate). */
  onDone?: (event: ChatStreamEvent) => void
  /** An in-band engine failure (the streamed equivalent of the non-streaming 503). */
  onError?: (event: ChatStreamEvent) => void
  signal?: AbortSignal
}

/**
 * The chat-with-your-data turn: POSTs to `/api/chat/stream` and consumes the Server-Sent-Events
 * response, invoking the matching callback per event as it arrives (live tool steps, then answer deltas,
 * then a terminal `done`). Modelled on the Krona `streamJson` reader (fetch + `credentials:'include'` +
 * `res.body.getReader()`), but parses SSE framing (`\n\n`-separated frames, `data:` lines) instead of a
 * JSON stream. A non-OK HTTP status (e.g. 503 before the stream opens) throws {@link HttpStreamError}.
 */
export async function streamChat(request: ChatRequest, cb: StreamChatCallbacks): Promise<void> {
  const res = await fetch(`${Environment.API_URL}/api/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...sentryTraceHeaders() },
    credentials: 'include',
    body: JSON.stringify(request),
    signal: cb.signal
  })

  if (!res.ok) {
    let message = `Request failed (${res.status})`
    try {
      const data = await res.json()
      if (typeof data?.message === 'string') message = data.message
    } catch {
      // body was not JSON — keep the generic message
    }
    throw new HttpStreamError({ status: res.status, message })
  }
  if (!res.body) {
    throw new HttpStreamError({ status: res.status, message: 'Streaming body is empty', code: 'EMPTY_STREAM' })
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += value.replace(/\r\n/g, '\n')
      let sep = buffer.indexOf('\n\n')
      while (sep >= 0) {
        dispatchFrame(buffer.slice(0, sep), cb)
        buffer = buffer.slice(sep + 2)
        sep = buffer.indexOf('\n\n')
      }
    }
    if (buffer.trim().length > 0) dispatchFrame(buffer, cb)
  } finally {
    reader.releaseLock()
  }
}

/** Parse one SSE frame (its `data:` line(s)) into a {@link ChatStreamEvent} and route it to a callback. */
function dispatchFrame(frame: string, cb: StreamChatCallbacks): void {
  const payload = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice('data:'.length).replace(/^ /, ''))
    .join('\n')
  if (payload.length === 0) return

  let event: ChatStreamEvent
  try {
    event = JSON.parse(payload) as ChatStreamEvent
  } catch (e) {
    console.error('Failed to parse chat SSE frame', payload, e)
    return
  }

  // Debug trace of the agent's stream (visible with the console's Verbose/Debug level enabled).
  console.debug('[chat]', event.type, event.tool ?? event.message ?? `${(event.text ?? '').length} chars`)

  switch (event.type) {
    case 'step':
      cb.onStep?.(event)
      break
    case 'delta':
      cb.onDelta?.(event.text ?? '')
      break
    case 'done':
      cb.onDone?.(event)
      break
    case 'error':
      cb.onError?.(event)
      break
    default:
      break
  }
}
